/**
 * BidiText — inline wrapper that gives a piece of text its own auto-detected
 * base direction (bidi isolate), so mixed Hebrew/English (or any LTR/RTL) text
 * lays out logically regardless of the surrounding document direction.
 *
 * Use this for any label whose content is user-generated or language-mixed and
 * is rendered inside the app chrome — menu items, nav labels, select options,
 * entity names. Native `dir="auto"` makes the browser determine the base
 * direction from the first strong character and run the full Unicode
 * Bidirectional Algorithm on the content, which is exactly what's needed when
 * English letters are interleaved with Hebrew.
 *
 * It is INLINE and only affects the text run's own bidi ordering — it never
 * reorders the surrounding flex layout (icons, badges keep following the menu's
 * direction), so it's safe to drop into existing label spans.
 *
 * Usage:
 *   <BidiText className="truncate">{workspace.name}</BidiText>
 */

import React from 'react';

type BidiTextProps = React.HTMLAttributes<HTMLSpanElement> & {
  // Optional so the span can serve as a `<Trans components={{ n: <BidiText /> }}>`
  // placeholder — react-i18next injects the children via cloneElement at runtime.
  children?: React.ReactNode;
};

// `forwardRef` so the label can be used directly as a Radix `asChild` Slot
// child (e.g. a `<TooltipTrigger asChild>` / `<PopoverTrigger asChild>`)
// without React warning that a function component cannot receive a ref.
export const BidiText = React.forwardRef<HTMLSpanElement, BidiTextProps>(
  function BidiText({ children, ...props }, ref) {
    // `dir="auto"` also makes the element a bidi isolate (UA stylesheet sets
    // `unicode-bidi: isolate`), so it both auto-detects the base direction and
    // shields the text from the surrounding direction.
    return (
      <span dir="auto" ref={ref} {...props}>
        {children}
      </span>
    );
  }
);
